import React, { useEffect, useRef, useState } from 'react';
import { Field, AddressPicker, EMPTY_ADDR, SITE_COUNTRY, COUNTRY, cleanName, isValidName, NAME_MAX } from './fields';
import { isValidContactPhone } from '../utils/identify';
import ShippingMethods, { isPickup } from './ShippingMethods';
import { isBoxnow } from './ship-classify';
import AddressModal from './AddressModal';
import { suggestEmail } from './emailcheck';
import { saveAddress } from './auth';

const hasAddr = (a) => !!(a && (a.address_1 || '').trim());

const cfg = (typeof window !== 'undefined' && window.CHECKOUT_NEXT) || {};
const BOOK = (cfg.addresses) || {};
const INIT_BILLING_BOOK = Array.isArray(BOOK.billing) ? BOOK.billing : [];
const INIT_SHIPPING_BOOK = Array.isArray(BOOK.shipping) ? BOOK.shipping : [];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Step1Billing({ value, onChange, shipping, shippingLoading, onRecalc, onNext }) {
  const up = (patch) => onChange({ ...value, ...patch });

  const docType = value.docType || 'receipt';
  const billing = value.billing || EMPTY_ADDR;
  const invoice = value.invoice || {};

  // Store pickup: no delivery address is collected. We only ask for the
  // customer's address when it's a business invoice (ΑΦΜ/company seat).
  const shipMethods = (shipping && shipping.methods) || [];
  const selMethodId = value.shippingMethod || (shipping && shipping.chosen) || (shipMethods[0] && shipMethods[0].id) || '';
  const selMethod = shipMethods.find((m) => m.id === selMethodId);
  const pickupSelected = selMethod
    ? isPickup(selMethod.id)
    : (selMethodId || '').toLowerCase().indexOf('local_pickup') !== -1;
  // Whether we need a customer address at all (pickup+receipt needs none).
  const needsAddress = !pickupSelected || docType === 'invoice';

  // Pickup always ships to the store → never a separate shipping address.
  useEffect(() => {
    if (pickupSelected && !value.shipToBilling) onChange({ ...value, shipToBilling: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickupSelected]);

  const [errors, setErrors] = useState([]);
  const [attempted, setAttempted] = useState(false); // turns section fields red once Next is pressed
  const [editing, setEditing] = useState(null); // 'billing' | 'shipping' | null
  // After saving an address in the modal, the next field in reading order is
  // Email (Επικοινωνία sits right below both address sections) — jump the
  // cursor there so the shopper can keep typing without reaching for the mouse.
  const emailRef = useRef(null);
  const focusEmailAfterSave = useRef(false);
  useEffect(() => {
    if (editing === null && focusEmailAfterSave.current) {
      focusEmailAfterSave.current = false;
      if (emailRef.current) emailRef.current.focus();
    }
  }, [editing]);
  // Saved-address book (unified for billing + shipping). Grows as the shopper
  // enters addresses; persisted server-side for logged-in users.
  const [billingBook, setBillingBook] = useState(INIT_BILLING_BOOK);
  const [shippingBook, setShippingBook] = useState(INIT_SHIPPING_BOOK);

  // Persist an entered address + refresh both dropdowns (latest on top).
  function rememberAddress(a) {
    if (!a || !(a.address_1 || '').trim()) return;
    saveAddress(a).then(({ ok, data }) => {
      if (ok && data.addresses) {
        setBillingBook(Array.isArray(data.addresses.billing) ? data.addresses.billing : []);
        setShippingBook(Array.isArray(data.addresses.shipping) ? data.addresses.shipping : []);
      }
    });
  }
  // Delivery note is collapsed behind a link until the shopper wants to add one.
  const [noteOpen, setNoteOpen] = useState(() => !!(value.deliveryNote || '').trim());

  function onBillingSelect(id) {
    if (id === 'new') { up({ billingAddrId: 'new', billing: { ...EMPTY_ADDR, email: billing.email } }); setEditing('billing'); return; }
    const a = billingBook.find((x) => x.id === id);
    up({ billingAddrId: id, billing: { ...EMPTY_ADDR, ...(a ? a.fields : {}) } });
  }
  function onShippingSelect(id) {
    if (id === 'new') { up({ shippingAddrId: 'new', shipping: { ...EMPTY_ADDR } }); setEditing('shipping'); return; }
    const a = shippingBook.find((x) => x.id === id);
    up({ shippingAddrId: id, shipping: { ...EMPTY_ADDR, ...(a ? a.fields : {}) } });
  }
  function saveBilling(a, inv, afmStatus) {
    const invoice = inv || value.invoice;
    const patch = { billing: { ...a, email: billing.email }, invoice, billingAddrId: 'new' };
    // Mark the invoice AADE-verified when the ΑΦΜ lookup succeeded (or a resolved company
    // is present, e.g. a reused validated profile). Downstream 39Α + island-VAT eligibility
    // key off lookupStatus === 'valid'; without this a first-time invoice buyer never
    // triggered the island reduction. Fail-closed to 'invalid' otherwise.
    if ( afmStatus === 'valid' || ( invoice && ( invoice.billing_company || '' ).trim() ) ) {
      patch.lookupStatus = 'valid';
    } else if ( afmStatus ) {
      patch.lookupStatus = 'invalid';
    }
    up(patch); setEditing(null); rememberAddress(a);
    focusEmailAfterSave.current = true;
  }
  function saveShipping(a) { up({ shipping: a, shippingAddrId: 'new' }); setEditing(null); rememberAddress(a); focusEmailAfterSave.current = true; }
  // Edit from within the dropdown: select that address first (id may be null = the current one), then open the editor.
  function onBillingEdit(id) { if (id) onBillingSelect(id); setEditing('billing'); }
  function onShippingEdit(id) { if (id) onShippingSelect(id); setEditing('shipping'); }

  const AddrSummary = ({ a, company, vat, invalid, onEdit }) => {
    const name = [a.first_name, a.last_name].filter(Boolean).join(' ');
    const top = company || name;
    return (
      <div className={`cn-addr-summary ${invalid ? 'is-invalid' : ''}`}>
        <div className="cn-addr-summary__text">
          {top ? <strong>{top}</strong> : null}
          {company && name ? <span>{name}</span> : null}
          <span>{[a.address_1, a.postcode, a.city].filter(Boolean).join(', ')}</span>
          {vat ? <span>ΑΦΜ: {vat}</span> : null}
        </div>
        <button type="button" className="cn-link" onClick={onEdit}>Επεξεργασία</button>
      </div>
    );
  };

  const hasName = (a) => isValidName(a.first_name) && isValidName(a.last_name);
  // For invoice, "complete" means the ΑΦΜ was resolved (company known). For a
  // pickup receipt we only need a name (no address).
  const billingComplete = docType === 'invoice'
    ? !!invoice.billing_company
    : (needsAddress ? hasAddr(billing) : hasName(billing));

  // Per-section validity, so we can flag the address control red after a failed Next.
  const pcOk = (v) => (String(v || '').match(/\d/g) || []).join('').length >= 4;
  const addrValid = (a) => !!(hasName(a) && (a.address_1 || '').trim() && pcOk(a.postcode) && (a.city || '').trim());
  const billingValid = docType === 'invoice'
    ? (addrValid(billing) && !!invoice.billing_company)
    : (needsAddress ? addrValid(billing) : hasName(billing));
  const shippingValid = value.shipToBilling || addrValid(value.shipping || EMPTY_ADDR);
  const billingInvalid = attempted && !billingValid;
  const shippingInvalid = attempted && !shippingValid;
  const emailComplete = EMAIL_RE.test((billing.email || '').trim());
  const emailSuggestion = suggestEmail(billing.email);
  // Existing (logged-in) users already have their contact on file — hide the
  // Επικοινωνία section once email + phone are known. Still shown if a logged-in
  // user is missing a valid phone (it's required to place the order).
  const contactPhoneValid = isValidContactPhone(value.contactPhone, SITE_COUNTRY);
  // For store pickup we always show Επικοινωνία — we need a reachable phone to
  // call the customer when the order is ready to collect (never hide it).
  const hideContact = !pickupSelected && !!cfg.isLoggedIn && !cfg.forceGuest && emailComplete && contactPhoneValid;

  // Effective delivery destination drives the shipping-method recalculation.
  const dest = value.shipToBilling ? billing : (value.shipping || EMPTY_ADDR);
  const destKey = [SITE_COUNTRY, dest.postcode, dest.city, dest.state].join('|');
  const lastDestKey = useRef(destKey);
  const recalcTimer = useRef(null);
  // Preserve the customer's chosen method (esp. BoxNow, preselected in the cart) across
  // address-driven recalcs. Without passing it, the server recalc returns its own default
  // `chosen` (a courier) → the BoxNow row would vanish under "hidden couriers" the moment
  // the address/locker resolves. Ref so the 500ms-debounced call reads the latest value.
  const methodRef = useRef('');
  methodRef.current = value.shippingMethod || (shipping && shipping.chosen) || '';

  useEffect(() => {
    const changed = destKey !== lastDestKey.current;
    const pcLen = (dest.postcode || '').replace(/\D/g, '').length;
    const needInitial = (!shipping || !(shipping.methods || []).length) && pcLen >= 3;
    if (!changed && !needInitial) return;
    lastDestKey.current = destKey;
    clearTimeout(recalcTimer.current);
    if (pcLen < 3) return; // wait for a plausible postcode
    recalcTimer.current = setTimeout(() => {
      onRecalc({ postcode: dest.postcode, city: dest.city, state: dest.state }, methodRef.current);
    }, 500);
    return () => clearTimeout(recalcTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destKey]);

  return (
    <section className="cn-step1">
      {/* Q1 — Απόδειξη ή Τιμολόγιο */}
      <fieldset className="cn-doctype">
        <legend className="cn-doctype__legend">Απόδειξη ή Τιμολόγιο;</legend>
        <div className="cn-doctype__options">
          {['receipt', 'invoice'].map((t) => (
            <label key={t} className={`cn-choice ${docType === t ? 'is-selected' : ''}`}>
              <input type="radio" name="cn-doctype" value={t} checked={docType === t} onChange={() => up({ docType: t })} />
              <span className="cn-choice__title">{t === 'receipt' ? 'Απόδειξη' : 'Τιμολόγιο'}</span>
            </label>
          ))}
        </div>
      </fieldset>

      {/* Pickup + receipt: no address needed — just who's collecting the order. */}
      {!needsAddress ? (
        <div className="cn-grid">
          <Field label="Όνομα" value={billing.first_name} onChange={(val) => up({ billing: { ...billing, first_name: cleanName(val) } })} maxLength={NAME_MAX} autoComplete="given-name" valid={isValidName(billing.first_name)} />
          <Field label="Επώνυμο" value={billing.last_name} onChange={(val) => up({ billing: { ...billing, last_name: cleanName(val) } })} maxLength={NAME_MAX} autoComplete="family-name" valid={isValidName(billing.last_name)} />
        </div>
      ) : (
        /* Billing details. Receipt with a saved book → picker; invoice or guest →
           summary/fake-input that opens the popup (ΑΦΜ lives there for invoice). */
        docType !== 'invoice' && billingBook.length > 0 ? (
          <AddressPicker options={billingBook} current={billing} valueId={value.billingAddrId} onSelect={onBillingSelect} onEdit={onBillingEdit} invalid={billingInvalid} />
        ) : (
          billingComplete
            ? <AddrSummary a={billing} company={docType === 'invoice' ? invoice.billing_company : ''} vat={docType === 'invoice' ? invoice.billing_vat : ''} invalid={billingInvalid} onEdit={() => setEditing('billing')} />
            : (
              <label className="cn-field cn-field--full">
                <span className="cn-field__label">{docType === 'invoice' ? 'ΑΦΜ & στοιχεία χρέωσης' : 'Διεύθυνση'}</span>
                <button type="button" className={`cn-input cn-fakeinput ${billingInvalid ? 'is-invalid' : ''}`} onClick={() => setEditing('billing')}>
                  {docType === 'invoice' ? 'Πρόσθεσε ΑΦΜ & στοιχεία χρέωσης…' : 'Πρόσθεσε τη διεύθυνσή σου…'}
                </button>
              </label>
            )
        )
      )}

      {/* Different shipping address — discreet dotted toggle, last item of billing.
          Hidden for store pickup (ships to the store, no delivery address). */}
      {pickupSelected ? null : value.shipToBilling ? (
        <button type="button" className="cn-diffaddr-toggle" onClick={() => up({ shipToBilling: false })}>
          Διαφορετική διεύθυνση αποστολής;
        </button>
      ) : (
        <div className="cn-diffaddr">
          <div className="cn-diffaddr__head">
            <span className="cn-diffaddr__title">Διεύθυνση αποστολής</span>
            <button type="button" className="cn-link" onClick={() => up({ shipToBilling: true })}>Απόκρυψη</button>
          </div>
          {shippingBook.length > 0 ? (
            <AddressPicker options={shippingBook} current={value.shipping} valueId={value.shippingAddrId} onSelect={onShippingSelect} onEdit={onShippingEdit} invalid={shippingInvalid} />
          ) : (
            hasAddr(value.shipping)
              ? <AddrSummary a={value.shipping} invalid={shippingInvalid} onEdit={() => setEditing('shipping')} />
              : (
                <label className="cn-field cn-field--full">
                  <span className="cn-field__label">Διεύθυνση αποστολής</span>
                  <button type="button" className={`cn-input cn-fakeinput ${shippingInvalid ? 'is-invalid' : ''}`} onClick={() => setEditing('shipping')}>Πρόσθεσε διεύθυνση αποστολής…</button>
                </label>
              )
          )}
        </div>
      )}

      {/* Account contact — email + phone (the address carries its own delivery
          phone). Hidden for existing users whose contact is already on file. */}
      {!hideContact && (
        <>
          <h3 className="cn-section-title">Επικοινωνία</h3>
          <div className="cn-grid">
            <Field ref={emailRef} label="Email" value={billing.email} onChange={(val) => up({ billing: { ...billing, email: val } })} type="email" inputMode="email" autoComplete="email" placeholder="you@example.com" valid={emailComplete} />
            <Field label="Τηλέφωνο" value={value.contactPhone} onChange={(val) => up({ contactPhone: val })} type="tel" inputMode="tel" autoComplete="tel" placeholder={`π.χ. ${COUNTRY.phoneExample}`} valid={contactPhoneValid} />
          </div>
          {emailSuggestion ? (
            <p className="cn-email-suggest">
              Μήπως εννοείς{' '}
              <button type="button" className="cn-link" onClick={() => up({ billing: { ...billing, email: emailSuggestion } })}>{emailSuggestion}</button>;
            </p>
          ) : null}
        </>
      )}

      {/* Shipping method — first selected, others folded. Greyed + click-locked
          until the billing details are set (no destination = no real rates). */}
      <h3 className="cn-section-title">Μέθοδος αποστολής</h3>
      <div className={`cn-ship-section ${billingComplete ? '' : 'is-locked'}`} aria-disabled={!billingComplete}>
        {!billingComplete && <p className="cn-ship-lockhint">Συμπλήρωσε πρώτα τα στοιχεία χρέωσης.</p>}
        <ShippingMethods
          data={shipping}
          loading={shippingLoading}
          value={value.shippingMethod}
          dest={dest}
          boxnowLocker={value.boxnowLocker}
          onBoxnowLocker={(loc) => up({ boxnowLocker: loc })}
          onChange={(id) => { up({ shippingMethod: id }); onRecalc({ postcode: dest.postcode, city: dest.city, state: dest.state }, id); }}
        />
      </div>

      {/* Delivery note (max 60, like wc-checkout order comments) — collapsed
          behind a link until the shopper chooses to add one. */}
      {noteOpen ? (
        <label className="cn-field cn-field--full">
          <span className="cn-field__label">Σημείωση παράδοσης (προαιρετικό)</span>
          <textarea
            className="cn-input cn-textarea"
            maxLength={60}
            rows={2}
            autoFocus
            value={value.deliveryNote || ''}
            placeholder="π.χ. κουδούνι δίπλα στην πόρτα, 2ος όροφος"
            onChange={(e) => up({ deliveryNote: e.target.value })}
          />
          <span className="cn-charcount">{(value.deliveryNote || '').length}/60</span>
        </label>
      ) : (
        <button type="button" className="cn-link cn-addnote" onClick={() => setNoteOpen(true)}>
          + Προσθήκη σημείωσης παράδοσης
        </button>
      )}

      {errors.length > 0 && (
        <div className="cn-errors" role="alert">
          <ul>{errors.map((m, i) => <li key={i}>{m}</li>)}</ul>
        </div>
      )}

      <button type="button" className="cn-btn cn-btn--primary cn-next" onClick={handleNext} disabled={shippingLoading} aria-busy={shippingLoading}>
        {shippingLoading && <span className="cn-btn__spinner" aria-hidden="true" />}
        {shippingLoading ? 'Υπολογισμός μεταφορικών…' : 'Συνέχεια στην πληρωμή'}
      </button>

      {editing === 'billing' && (
        <AddressModal
          title={docType === 'invoice' ? 'Στοιχεία χρέωσης (Τιμολόγιο)' : 'Διεύθυνση χρέωσης'}
          initial={billing}
          invoiceMode={docType === 'invoice'}
          invoice={invoice}
          onClose={() => setEditing(null)}
          onSave={saveBilling}
        />
      )}
      {editing === 'shipping' && (
        <AddressModal title="Διεύθυνση αποστολής" initial={value.shipping} onClose={() => setEditing(null)} onSave={saveShipping} showPhone />
      )}
    </section>
  );

  function validate() {
    const e = [];
    const b = value.billing || {};
    const pcOk = (v) => (String(v || '').match(/\d/g) || []).join('').length >= 4;

    if (!isValidName(b.first_name) || !isValidName(b.last_name)) e.push('Συμπλήρωσε ονοματεπώνυμο (μόνο γράμματα).');
    if (needsAddress) {
      if (!(b.address_1 || '').trim()) e.push('Συμπλήρωσε τη διεύθυνση χρέωσης.');
      if (!pcOk(b.postcode)) e.push('Συμπλήρωσε έγκυρο Τ.Κ. χρέωσης.');
      if (!(b.city || '').trim()) e.push('Συμπλήρωσε την πόλη χρέωσης.');
    }
    if (!EMAIL_RE.test((b.email || '').trim())) e.push('Συμπλήρωσε ένα έγκυρο email.');
    if (!(value.contactPhone || '').trim()) e.push('Συμπλήρωσε τηλέφωνο επικοινωνίας.');

    if (docType === 'invoice' && !invoice.billing_company) {
      e.push('Συμπλήρωσε έγκυρο ΑΦΜ για το τιμολόγιο.');
    }

    if (!value.shipToBilling) {
      const s = value.shipping || {};
      if (!isValidName(s.first_name) || !isValidName(s.last_name)) e.push('Συμπλήρωσε ονοματεπώνυμο παραλήπτη (μόνο γράμματα).');
      if (!(s.address_1 || '').trim()) e.push('Συμπλήρωσε τη διεύθυνση αποστολής.');
      if (!pcOk(s.postcode)) e.push('Συμπλήρωσε έγκυρο Τ.Κ. αποστολής.');
      if (!(s.city || '').trim()) e.push('Συμπλήρωσε την πόλη αποστολής.');
    }

    const methods = (shipping && shipping.methods) || [];
    const mId = value.shippingMethod || (shipping && shipping.chosen) || (methods[0] && methods[0].id) || '';
    if (!methods.length || !mId) {
      e.push('Επίλεξε μέθοδο αποστολής.');
    } else {
      const sm = methods.find((m) => m.id === mId);
      if (sm && isBoxnow(sm.id) && !(value.boxnowLocker && value.boxnowLocker.id)) {
        e.push('Επίλεξε σημείο παραλαβής Box Now.');
      }
    }
    return e;
  }

  function handleNext() {
    if (shippingLoading) return; // don't advance mid-recalc (cost/method still settling)
    const e = validate();
    setErrors(e);
    setAttempted(true);
    if (e.length === 0) {
      onNext && onNext();
    } else if (typeof window !== 'undefined' && window.scrollTo) {
      // bring the error summary into view
      const el = document.querySelector('.cn-errors');
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }
}
